import { HUES_BY_COLOUR, RED_HUES, ROSE_HUES, WHITE_HUES, WINE_COLOUR } from "@proef/domain";
import { z } from "zod";

/** Canonical identity of a wine as the app knows it. Keys are opaque ids, never GTINs. */
export const WineIdentity = z.object({
  id: z.string().min(1).max(200),
  producer: z.string().min(1).max(200),
  name: z.string().min(1).max(200),
  /** `null` for non-vintage; absent means unknown. */
  vintage: z.number().int().min(1800).max(2100).nullable().optional(),
  grapes: z.array(z.string().min(1).max(100)).max(30).default([]),
  country: z.string().length(2).describe("ISO 3166-1 alpha-2"),
  region: z.string().min(1).max(200).optional(),
  appellation: z.string().min(1).max(200).optional(),
  colour: z.enum(WINE_COLOUR),
  sparkling: z.boolean().default(false),
  /** GTIN-14 keys this wine has been seen under, for the barcode cache. */
  gtins: z
    .array(z.string().regex(/^[0-9]{14}$/))
    .max(20)
    .default([]),
});
export type WineIdentity = z.infer<typeof WineIdentity>;

/** What a taster judged a wine's colour to be: the pre-selection of the colour step next time. */
export const WineLook = z
  .object({
    colour: z.enum(WINE_COLOUR),
    sparkling: z.boolean(),
    hue: z.enum([...WHITE_HUES, ...ROSE_HUES, ...RED_HUES]),
  })
  .refine((look) => (HUES_BY_COLOUR[look.colour] as readonly string[]).includes(look.hue), {
    message: "hue does not belong to the colour",
    path: ["hue"],
  });
export type WineLook = z.infer<typeof WineLook>;

/** `GET /v1/wine-looks?ids=…`: the device's looks for the wines asked about; wines it never judged are absent. */
export const WineLooksResponse = z.object({ looks: z.record(z.string(), WineLook) });
export type WineLooksResponse = z.infer<typeof WineLooksResponse>;

/** The most wines one read of looks may name. */
export const WINE_LOOKS_MAX_IDS = 50;
